import React from 'react';
import type {
  FeatureConfig,
  Identity,
  IdentitySource,
  UpdateIdentityOptions,
} from '@packages/systems/feature-config/shared';
import {
  FEATURE_FLAGS,
  FeatureConfigSchema,
  formatIdentity,
  isEuAwsRegion,
} from '@packages/systems/feature-config/shared';
import {getFeatureFlagOutsideOfReact} from './flags/flags';
import {
  asSite,
  asUser,
  asWorkspace,
} from '@packages/systems/request-context/types';
import {updateOverrides} from './utils';
import {getStatsigClient, statsigClientExists} from './client';
import {readServerData} from '@packages/systems/server-data/client';
import {z} from 'zod';

const ServerDataSchema = z
  .object({featureConfig: FeatureConfigSchema})
  .passthrough();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let initialData = {} as any;
if (typeof window !== 'undefined') {
  let serverData: z.infer<typeof ServerDataSchema> | null = null;
  try {
    serverData = readServerData(ServerDataSchema, {fallback: null});
  } catch {
    // readServerData may fail (e.g. malformed JSON) — fall through to window.initialData
  }

  if (serverData) {
    initialData = serverData;
    window.initialData = initialData;
  } else if (window.initialData) {
    initialData = window.initialData;
  } else {
    window.initialData = initialData;
  }
}

const source: IdentitySource = {
  app: undefined,
  serviceName: undefined,
  anonymousId: undefined,
  customId: undefined,
  githubId: undefined,
  path: undefined,
  pathWithParams: undefined,
  site: undefined,
  user: undefined,
  userAgent: undefined,
  workspace: undefined,
  // timestamps intended for debugging purposes only
  ruleCacheUpdated: undefined,
  hydrationDataCreated: undefined,
  webflowNextEnrolledFeatures: undefined,
  isEnterpriseWorkspace: undefined,
  isEnterpriseSite: undefined,
  webflowCloudAuthorization: undefined,
  sessionReplayConsent: undefined,
  awsRegion: undefined,
};

export const getFormattedIdentity = () => formatIdentity(source);

/**
 * Returns true when the client is running against the EU stack (eu-west-1).
 *
 * Reads `awsRegion` from the feature-config identity, which the server attaches
 * from its own `AWS_REGION` at hydration time. `setIdentity` populates this
 * during `initFeatureConfig`, so the value is available once feature config has
 * initialized. Mirrors the server-side `isEuStack()` guard.
 */
export const isEuStackClient = (): boolean =>
  isEuAwsRegion(getFormattedIdentity().custom.awsRegion);

/**
 * Reports whether the identity marks the workspace as enterprise.
 *
 * The server sends `isEnterpriseWorkspace` as the string `'true'` or `'false'`,
 * and leaves it out on pages that do not run `loadEnterpriseContext` (preview
 * and comment pages). A missing value counts as not enterprise.
 */
export const isEnterpriseWorkspaceIdentity = (
  identity: Pick<Identity, 'custom'>
): boolean => identity.custom.isEnterpriseWorkspace === 'true';

export const useIdentity_doesNotUpdateWhenIdentityChanges = (): Identity => {
  // ideally we'd want this to change whenever the identity updates,
  // but unfortunately that causes the dashboard to re-fetch every
  // time the user toggles a flag. to solve this, we could memoise
  // formatIdentity itself, but that may require some further
  // changes and didn't feel worth it for now!
  return React.useMemo(() => formatIdentity(source), []);
};

export const setIdentity = (update: Identity) => {
  source.app = update.custom.app;
  source.serviceName = update.custom.serviceName;
  source.anonymousId = update.customIDs.anonymousId;
  source.customId = update.customIDs.customId;
  source.githubId = update.customIDs.githubId;
  source.path = update.custom.path;
  source.pathWithParams = update.custom.pathWithParams;
  source.userAgent = update.userAgent;
  source.ip = update.ip;
  // timestamps intended for debugging purposes only
  source.ruleCacheUpdated = update.custom.ruleCacheUpdated;
  source.hydrationDataCreated = update.custom.hydrationDataCreated;

  if (update.userID === 'unauthenticated-users') {
    source.user = undefined;
  } else {
    source.user = asUser({
      _id: update.userID,
      createdOn: update.custom?.userCreated,
      verified: update.custom?.userVerified === 'true',
      inPendoAudience: update.custom?.userPendoAudience === 'true',
      sessionReplayConsent: update.custom?.sessionReplayConsent === 'true',
    });
  }

  source.site = asSite({
    _id: update.customIDs?.siteId,
    createdOn: update.custom?.siteCreated,
    shortName: update.custom?.siteShortName,
    cloneOf: update.custom?.cloneOf,
    templateOf: update.custom?.templateOf,
    kind: update.custom?.siteKind,

    // Site realtimeDisabled field is the inverse of our Statsig Identity isRealtimeEnabled field
    realtimeDisabled:
      !update.custom?.isRealtimeEnabled ||
      update.custom?.isRealtimeEnabled === 'false',
    isInRealtimeRollout: update.custom?.isInRealtimeRollout === 'true',
  });

  source.workspace = asWorkspace({
    _id: update.customIDs?.workspaceId,
    createdOn: update.custom?.workspaceCreated,
    slug: update.custom?.workspaceSlug,
    isOveragesEnabled: update.custom?.isOveragesEnabled === 'true',
    partnerSandboxFor: update.custom?.partnerSandboxFor,
  });

  source.webflowNextEnrolledFeatures =
    update.custom?.webflowNextEnrolledFeatures;

  source.isEnterpriseWorkspace = update.custom?.isEnterpriseWorkspace;
  source.isEnterpriseSite = update.custom?.isEnterpriseSite;
  source.webflowCloudAuthorization = update.custom?.webflowCloudAuthorization;
  source.sessionReplayConsent = update.custom?.sessionReplayConsent;
  source.awsRegion = update.custom?.awsRegion;
};

// Only the server sets these fields. Copy them from each server bootstrap so
// identities built on the client later match the bootstrap's user, which
// keeps Statsig from reporting a bootstrap mismatch.
const syncServerDerivedFields = (serverIdentity?: Identity) => {
  if (!serverIdentity) return;
  source.ruleCacheUpdated = serverIdentity.custom.ruleCacheUpdated;
  source.hydrationDataCreated = serverIdentity.custom.hydrationDataCreated;
  source.isEnterpriseWorkspace = serverIdentity.custom.isEnterpriseWorkspace;
  source.isEnterpriseSite = serverIdentity.custom.isEnterpriseSite;
};

const stripTrailingSlash = (url?: string): string | undefined =>
  url?.replace(/\/$/, '');

type IdentityUpdate = {
  source?: string;
  resolved: boolean;
};

export const updateIdentity = async (
  update: IdentitySource,
  options: UpdateIdentityOptions = {}
): Promise<IdentityUpdate> => {
  let identityHasChanged = false;

  if (Object.hasOwn(update, 'app') && source.app !== update.app) {
    source.app = update.app;
    identityHasChanged = true;
  }

  if (
    Object.hasOwn(update, 'serviceName') &&
    source.serviceName !== update.serviceName
  ) {
    source.serviceName = update.serviceName;
    identityHasChanged = true;
  }

  if (
    Object.hasOwn(update, 'anonymousId') &&
    source.anonymousId !== update.anonymousId
  ) {
    source.anonymousId = update.anonymousId;
    identityHasChanged = true;
  }
  if (
    Object.hasOwn(update, 'customId') &&
    source.customId !== update.customId
  ) {
    source.customId = update.customId;
    identityHasChanged = true;
  }
  if (
    Object.hasOwn(update, 'githubId') &&
    source.githubId !== update.githubId
  ) {
    source.githubId = update.githubId;
    identityHasChanged = true;
  }
  const path = stripTrailingSlash(update.path);
  if (Object.hasOwn(update, 'path') && source.path !== path) {
    source.path = path;
    identityHasChanged = true;
  }
  const pathWithParams = stripTrailingSlash(update.pathWithParams);
  if (
    Object.hasOwn(update, 'pathWithParams') &&
    source.pathWithParams !== pathWithParams
  ) {
    source.pathWithParams = pathWithParams;
    identityHasChanged = true;
  }
  if (Object.hasOwn(update, 'site') && source.site?._id !== update.site?._id) {
    source.site = update.site;
    identityHasChanged = true;
  }
  if (Object.hasOwn(update, 'user') && source.user?._id !== update.user?._id) {
    source.user = update.user;
    identityHasChanged = true;
  }
  if (
    Object.hasOwn(update, 'workspace') &&
    source.workspace?._id !== update.workspace?._id
  ) {
    source.workspace = update.workspace;
    identityHasChanged = true;
  }

  if (Object.hasOwn(update, 'isEnterpriseWorkspace')) {
    source.isEnterpriseWorkspace = update.isEnterpriseWorkspace;
    identityHasChanged = true;
  }

  if (Object.hasOwn(update, 'isEnterpriseSite')) {
    source.isEnterpriseSite = update.isEnterpriseSite;
    identityHasChanged = true;
  }

  if (Object.hasOwn(update, 'webflowCloudAuthorization')) {
    source.webflowCloudAuthorization = update.webflowCloudAuthorization;
    identityHasChanged = true;
  }

  if (Object.hasOwn(update, 'sessionReplayConsent')) {
    source.sessionReplayConsent = update.sessionReplayConsent;
    identityHasChanged = true;
  }

  if (
    (identityHasChanged && getStatsigClient()) ||
    options?.forceLoadServerConfig
  ) {
    const identity = formatIdentity(source);

    if (typeof initialData !== 'undefined') {
      initialData.featureConfig = {
        ...initialData.featureConfig,
        identity,
      };
    }

    // the following code surrounding the Statsig.updateUser call
    // is intended to handle situations where we call updateUser
    // multiple times in a short period of time (e.g. when many
    // components call this in side-effects multiple times in
    // a single render pass).
    //
    // without this code, we saw the following behaviour:
    // - statsig resolves the first updated identity
    // - statsig calls the "on user update" callback
    //   - this includes our hooks, therefore react state
    //     is updated, and a render is scheduled
    // - statsig starts resolving the second identity
    // - react renders the UI while statsig is still fetching
    //   the second identity
    // - statsig resolves the second identity, etc
    //
    // since that react render happens while statsig is
    // in an intermediate state, it returns all flags
    // as `false`; this happens within a fifth of a
    // second, and causes the UI to flicker very
    // noticeably!
    //
    // instead, we want to fetch the flags & gates for the
    // updated identity first, and bail out of the process
    // if updateUser was called again before the fetch had
    // time to resolve.
    userUpdateCount++;
    const identityId = userUpdateCount;
    return loadServerConfigOrFallback(identity).then(
      async ({fallback, config}) => {
        if (identityId !== userUpdateCount) return {resolved: true};

        if (fallback) {
          // using identity here instead of config?.values?.user, as config is undefined in this case
          getStatsigClient().updateUserAsync(identity);
          return {resolved: true};
        }

        if (config.values) {
          getStatsigClient().dataAdapter.setData(JSON.stringify(config.values));
          syncServerDerivedFields(config.identity);
        }

        getStatsigClient().updateUserSync(config?.values?.user ?? identity, {
          disableBackgroundCacheRefresh: true,
        });

        if (config.overrides) {
          updateOverrides(config.overrides);
        }

        return {
          source: 'identityHasChanged',
          resolved: true,
        };
      }
    );

    // since we don't want to block rendering while the
    // update is being processed, we don't return the
    // promise from loadServerConfig, and just
    // fall through to the immediately resolved
    // promise below
  }
  return Promise.resolve({resolved: true});
};

let userUpdateCount = 0;

export async function forceRefresh(): Promise<boolean | void> {
  if (statsigClientExists()) {
    const identity = formatIdentity(source);

    loadServerConfigOrFallback(identity).then(({fallback, config}) => {
      if (fallback) {
        getStatsigClient().updateUserAsync(identity);
        return;
      }

      if (config.values) {
        getStatsigClient().dataAdapter.setData(JSON.stringify(config.values));
        syncServerDerivedFields(config.identity);
      }

      getStatsigClient().updateUserSync(config?.values?.user ?? identity, {
        disableBackgroundCacheRefresh: true,
      });

      if (config.overrides) {
        updateOverrides(config.overrides);
      }
    });
  }
}

function fallBack(
  identity: Identity
): Promise<{fallback: true; config: undefined}> {
  return getStatsigClient()
    .dataAdapter.prefetchData(identity)
    .then(() => {
      return {fallback: true as const, config: undefined};
    });
}

async function loadServerConfigOrFallback(
  identity: Identity
): Promise<
  {fallback: true; config: undefined} | {fallback: false; config: FeatureConfig}
> {
  const shouldLoadServerConfig = getFeatureFlagOutsideOfReact(
    FEATURE_FLAGS.LOAD_SERVER_CONFIG
  );

  let response: Response;

  if (shouldLoadServerConfig) {
    return fetch(buildConfigUrl(identity), {
      method: 'GET',
      headers: {'Content-Type': 'application/json'},
    })
      .then((res) => {
        if (res.status >= 400) {
          response = res;
          throw new Error('Failed to fetch cached Statsig config');
        }
        return res.json();
      })
      .then((config: FeatureConfig) => {
        if (typeof initialData !== 'undefined') {
          initialData.featureConfig = config;
        }

        return {fallback: false as const, config};
      })
      .catch(async (err) => {
        let responseBody;
        try {
          responseBody = await response?.json();
        } catch (_) {
          responseBody = 'failed to parse';
        } finally {
          console.error(
            '[Statsig] Hydrating config from cache failed - falling back to Statsig.prefetchUsers',
            err,
            {
              responseStatus: response?.status,
              responseBody,
            }
          );
          return fallBack(identity);
        }
      });
  }

  return fallBack(identity);
}

function buildConfigUrl(identity: Identity): string {
  const {app = 'unknown', workspaceSlug} = identity.custom;
  const {workspaceId, siteId} = identity.customIDs;

  let queryString = '';

  if (identity.custom.path && identity.custom.pathWithParams) {
    queryString = `?path=${encodeURIComponent(identity.custom.path)}&pathWithParams=${encodeURIComponent(identity.custom.pathWithParams)}`;
  } else if (identity.custom.path) {
    queryString = `?path=${encodeURIComponent(identity.custom.path)}`;
  } else if (identity.custom.pathWithParams) {
    queryString = `?pathWithParams=${encodeURIComponent(identity.custom.pathWithParams)}`;
  }

  // this exists as a bit of a short circuit to manually pass the session replay consent through to the server,
  // as the db update that sets the consent in the user document doesn't happen immediately
  if (identity.custom.sessionReplayConsent === 'true') {
    queryString += `${queryString ? '&' : '?'}sessionReplayConsent=true`;
  }

  if (siteId) {
    return `/api/feature-config/config/${app}/site/${siteId}${queryString}`;
  }
  if (workspaceId || workspaceSlug) {
    return `/api/feature-config/config/${app}/workspace/${workspaceId || workspaceSlug}${queryString}`;
  }
  return `/api/feature-config/config/${app}${queryString}`;
}
